<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { slide } from 'svelte/transition';
	import Link from 'svelte-navaid/Link.svelte';
	// Stores
	import SportBetLegStore from '@/Stores/Sport/BetLegStore.js';
	// Constants
	import { BetType } from '@/Constants/Bet.js';
	import { UserRatingColours } from '@/Constants/User.js';
	import { RollingType } from '@/Constants/Market';
	// Components
	import Rating from '@/Partials/User/Rating.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Services
	import { CurrencyFormat, NumberFormat } from '@/Services/Helpers';

	export let selection_type;
	export let selection_id;
	export let loading = false;
	export let rolling_type_id = undefined;
	export let export_bet_list = undefined;

	function betReturn(bet) {
		return CurrencyFormat((bet.bet.bet_amount * bet.fixed_price) / 100);
	}

	let sortBy = { col: 'id', ascending: true };
	let sortedBets = $SportBetLegStore.where(selection_type, selection_id).sortBy(sortBy.col).all();

	const sort = (column) => {
		if (sortBy.col == column) {
			sortBy.ascending = !sortBy.ascending;
		} else {
			sortBy.col = column;
			sortBy.ascending = true;
		}

		if (sortBy.ascending) {
			if (column == 'bet.return') {
				sortedBets = $SportBetLegStore
					.where(selection_type, selection_id)
					.sortBy((bet) => bet.bet.bet_amount * bet.fixed_price)
					.all();
			} else {
				sortedBets = $SportBetLegStore.where(selection_type, selection_id).sortBy(sortBy.col).all();
			}
		} else {
			if (column == 'bet.return') {
				sortedBets = $SportBetLegStore
					.where(selection_type, selection_id)
					.sortByDesc((bet) => bet.bet.bet_amount * bet.fixed_price)
					.all();
			} else {
				sortedBets = $SportBetLegStore.where(selection_type, selection_id).sortByDesc(sortBy.col).all();
			}
		}
	};
	let firstTimeRun = false;
	const doBetsUpdate = () => {
		if (!firstTimeRun && $SportBetLegStore.where(selection_type, selection_id).count() > 0) {
			sort('id');
			firstTimeRun = true;
		}
	};

	function downloadBetList() {
		if (export_bet_list) {
			export_bet_list();
		}
	}

	$: loading && $SportBetLegStore.where(selection_type, selection_id) && doBetsUpdate();
</script>

<div transition:slide|local class="h-64 bg-white overflow-auto">
	{#await loading}
		<div class="flex items-center justify-center px-1 mt-1 mr-1">Loading...</div>
	{:then}
		<div class="flex flex-col">
			{#if export_bet_list && $SportBetLegStore.where(selection_type, selection_id).count() > 0}
				<div class="flex justify-end">
					<button
						class="focus:outline-none btn-primary p-2 rounded m-1.5"
						on:click|preventDefault|stopPropagation={downloadBetList}
					>
						Export as CSV
					</button>
				</div>
			{/if}
			<div class="flex flex-col h-full px-1 text-sm border border-light-grey">
				<table class="min-w-full divide-y divide-gray-300">
					<thead class="bg-gray-50">
						<tr>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click={() => sort('id')}
							>
								<div class="flex flex-row justify-start items-center">
									Ticket #
									{#if sortBy.col == 'id'}
										<span class="ml-3 text-secondary">
											{#if sortBy.ascending}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click={() => sort('bet.created_at')}
							>
								<div class="flex flex-row justify-start items-center">
									Date
									{#if sortBy.col == 'bet.created_at'}
										<span class="ml-3 text-secondary">
											{#if sortBy.ascending}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click={() => sort('bet.user.name')}
							>
								<div class="flex flex-row justify-start items-center">
									Client
									{#if sortBy.col == 'bet.user.name'}
										<span class="ml-3 text-secondary">
											{#if sortBy.ascending}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer"
								on:click={() => sort('bet.bet_amount')}
							>
								<div class="flex flex-row justify-start items-center">
									Bet
									{#if sortBy.col == 'bet.bet_amount'}
										<span class="ml-3 text-secondary">
											{#if sortBy.ascending}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th scope="col" class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer">
								<div class="flex flex-row justify-center items-center">Multi</div>
							</th>
							<th scope="col" class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900 cursor-pointer">
								<div class="flex flex-row justify-center items-center">Status</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900"
								on:click={() => sort('leg_selections.data.items.0.name')}
							>
								<div class="flex flex-row justify-start items-center">
									Selection
									{#if sortBy.col == 'leg_selections.data.items.0.name'}
										<span class="ml-3 text-secondary">
											{#if sortBy.ascending}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
							<th
								scope="col"
								class="px-3 pt-3.5 pb-1 text-left text-sm font-semibold text-gray-900"
								on:click={() => sort('bet.return')}
							>
								<div class="flex flex-row justify-start items-center">
									Return
									{#if sortBy.col == 'bet.return'}
										<span class="ml-3 text-secondary">
											{#if sortBy.ascending}
												<Icon name="chevron-up" classes="w-3 h-3 ml-1" />
											{:else}
												<Icon name="chevron-down" classes="w-3 h-3 ml-1" />
											{/if}
										</span>
									{/if}
								</div>
							</th>
						</tr>
					</thead>
					<tbody class="divide-y divide-gray-200 bg-white">
						{#each sortedBets as bet (bet.id)}
							<tr class="hover:bg-gray-100">
								<td class="whitespace-nowrap px-3 py-1 text-sm">{bet.bet.ticket}</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">{dayjs(bet.bet.created_at).format('D/M/YY H:mm')}</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									<div
										class="flex flex-row {UserRatingColours[bet.bet.user.rating.id].bg} {UserRatingColours[
											bet.bet.user.rating.id
										].text}"
									>
										<Link
											href="/users/{bet.bet.user.id}"
											class="flex items-center focus:outline-none hover:underline"
											target="_blank"
											title="Open Client Profile"
										>
											{bet.bet.user.pin}/{bet.bet.user.name}
											<Rating className="ml-1" rating={bet.bet.user.rating} />
											<span class="p-1 text-center rounded-md text-tiny"
												>{bet.bet.user.sub_rating ? ' (' + bet.bet.user.sub_rating.name + ') ' : ''}</span
											>
										</Link>
									</div>
								</td>

								<td class="whitespace-nowrap px-3 text-sm">
									<div class="flex flex-row pl-1 items-center">
										<span>{CurrencyFormat(bet.bet.bet_amount)}</span>
										<div class="mx-0.5 mb-px inline-block align-text-top">@</div>
										<span>{NumberFormat(bet.fixed_price)}</span>
										{#if bet.bet.is_partially_accepted}
											<div class="ml-2 text-sm text-danger">
												(Partialled from: {CurrencyFormat(bet.bet.requested_bet_amount)})
											</div>
										{/if}
										{#if bet.bet.use_bonus}
											<FontIcon icon={'gift'} className="ml-1.5 text-white bg-gray-500 p-1 rounded-sm text-tiny mr-1" />
										{/if}
										{#if bet.boosted}
											<FontIcon icon={'boost-up'} className="ml-1.5 text-blue-500" />
										{/if}
									</div>
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									<div class="flex flex-row justify-center items-center">
										{#if bet.bet.bet_type.id === BetType.Multi || bet.bet.bet_type.id === BetType.SameGameMulti}
											<Icon name="circle-tick" classes="text-green-500 w-3 h-3" />
										{/if}
									</div>
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">
									<div class="flex flex-row justify-center items-center">
										{bet.bet.status.name}
									</div>
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm flex">
									{bet.leg_selections.data.pluck('name').first()}
									{#if bet.line !== null}
										{#if rolling_type_id === RollingType.Handicap}
											({bet.line < 0 ? bet.line : `+${bet.line}`})
										{:else}
											({bet.line})
										{/if}
									{/if}
								</td>
								<td class="whitespace-nowrap px-3 py-1 text-sm">{betReturn(bet)}</td>
							</tr>
						{/each}
					</tbody>
				</table>
			</div>
		</div>
	{/await}
</div>
